import { atom } from "jotai"
import { getUniquePhotoId, UniquePhotoId } from "../previews/previews-state"
import { PhotoId } from "../undoable-state"
import { focusAtom } from "jotai-optics"
import { defaultCullingSettings, PhotoRecord, ProjectCullingSettings } from "@/lib/catalog/types"
import { store } from "../store"
import {
	allPhotosAtom,
	cullingInfoAtom,
	currentPhotoIdAtom,
	displayedPhotosAtom,
	getCullingInfo,
	photosAtom,
	selectedPhotosIDsAtom,
	updatePhotos,
} from "../photo/photos-state"
import { autoAdvanceAfterTaggingIfNeeded } from "../photo/store-photos"
import { cropImage } from "@/core/utils/resize-image"
import { Photo } from "@/core/photo"
import { getEditor } from "@/utils/useEditor"
import { CropExtractor } from "@/core/crop-extractor"
import { useAtomValue } from "jotai"
import { useMemo } from "react"
import { ProjectItem } from "../project/projects-service"
import { Preview, PreviewSize, PreviewTag } from "@/store/previews/previews-types"
import { PaddedFaceRect, padFaceRect } from "./culling-utils"
import { getRejectedIds, selectKeepersOnTheFly } from "@/lib/ai/ai-cluster"
import { isCullingModeAtom, projectAtom, updateCurrentProject } from "../project/projects-state"
import { debounce } from "@/utils/debounce"
import { getDefaultAdjustments } from "@/core/adjustments"
import { exportImage } from "@/core/export-image"
import { defaultPreviewOptions } from "../previews/previews-constants"
import { JpegEncoder } from "@/lib/jpeg/jpeg-encoder"
import { Adjustments } from "@/core/types"
import { photosViewAtom } from "../photo/photos-view-state"
import { CullingTag } from "@/utils/exif"
import { showToast } from "../ui-views-state"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer toast i18n, to be refactored to UI-layer translation
import i18n from "@/i18n"
// eslint-disable-next-line no-restricted-imports
import { cullingTagKeys, CULLING_AUTO_KEY } from "@/i18n/dual-role-strings"

export interface ClusterInfo {
	clusterLeaderId: PhotoId
	coverPhotoId: PhotoId
	clusterPhotoIds: PhotoId[]
	ratedPhotoCount: number
	keeperPhotoCount: number
}

export interface ClusterPhotoGroupInfo {
	clusterLeaderId: PhotoId
	index: number
	total: number
}

// Re-export enums from types.ts for backward compatibility
export { ClusterThreshold, BlurryRejectThreshold, FaceRejectThreshold } from "@/lib/catalog/types"

export const currentCullingInfoAtom = atom((get) => {
	const cullingInfo = get(cullingInfoAtom)
	const currentPhotoId = get(currentPhotoIdAtom)

	return currentPhotoId ? cullingInfo[currentPhotoId] : undefined
})

export const completedCullingPhotoIdsAtom = atom<Set<PhotoId>>(new Set<PhotoId>())
export const isCullingAutoExposureOnAtom = atom(false)
export const cullingAutoExposurePreviewUrlsAtom = atom<Record<string, string>>({})
export const hoveredGroupedClusterIdAtom = atom<PhotoId | null>(null)

export const cullingSettingsAtom = atom(
	(get) => {
		const project = get(projectAtom)
		const settings = {
			...defaultCullingSettings,
			...(project?.cullingSettings ?? {}),
		}
		return settings
	},
	(get, set, newSettings: Partial<ProjectCullingSettings> | ProjectCullingSettings) => {
		const project = get(projectAtom)
		if (project) {
			const updatedSettings = {
				...(project.cullingSettings ?? defaultCullingSettings),
				...newSettings,
			}
			set(projectAtom, { ...project, cullingSettings: updatedSettings })
			void updateCurrentProject({ cullingSettings: updatedSettings })
		}
	},
)

export const isShowingRejectsAtom = atom((get) => {
	const cullingSettings = get(cullingSettingsAtom)
	return (
		cullingSettings.rejectBlurryPhotos ||
		cullingSettings.rejectPoorFaceQuality ||
		cullingSettings.rejectPoorExposure
	)
})

export const isShowingRejectsOrKeepersAtom = atom((get) => {
	const isSuggestingKeepers = get(isSuggestingKeepersAtom)
	const isShowingRejects = get(isShowingRejectsAtom)
	return isSuggestingKeepers || isShowingRejects
})

export const groupSimilarPhotosAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("groupSimilarPhotos"),
)

export const isShowingClustersAtom = atom((get) => {
	return get(isCullingModeAtom) && get(groupSimilarPhotosAtom) && get(photosViewAtom) !== "grid"
})

export const sortByScoreInClusterAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("sortByScoreWithinGroup"),
)
// Direct access to thresholds from cullingSettingsAtom
export const photoClustersThresholdsAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("groupSimilarThreshold"),
)
export const isSuggestingKeepersAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("suggestKeepers"),
)
export const autoAdvanceAfterTaggingAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("autoAdvanceAfterTagging"),
)

export const rejectBlurryPhotosAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("rejectBlurryPhotos"),
)
export const blurryPhotoThresholdAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("blurryPhotoThreshold"),
)
export const rejectPoorFaceQualityAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("rejectPoorFaceQuality"),
)
export const poorFaceQualityThresholdAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("poorFaceQualityThreshold"),
)
export const rejectPoorExposureAtom = focusAtom(cullingSettingsAtom, (optic) =>
	optic.prop("rejectPoorExposure"),
)
export const isSuggestingKeepersOrRejectsAtom = atom((get) => {
	const suggestKeepers = get(isSuggestingKeepersAtom)
	const rejectBlurryPhotos = get(rejectBlurryPhotosAtom)
	const rejectPoorFaceQuality = get(rejectPoorFaceQualityAtom)
	const rejectPoorExposure = get(rejectPoorExposureAtom)
	return suggestKeepers || rejectBlurryPhotos || rejectPoorFaceQuality || rejectPoorExposure
})

export const defaultSuggestedKeeperCount = () => {
	const allPhotos = store.get(allPhotosAtom)
	if (allPhotos.length === 0) {
		return 0
	}
	return Math.max(Math.round((allPhotos.length * 0.25) / 5) * 5, 1)
}

export const suggestedKeeperCountAtom = atom(
	(get) => {
		const project = get(projectAtom)
		return project?.suggestedKeeperCount ?? 0
	},
	(get, set, count: number) => {
		const project = get(projectAtom)
		if (project) {
			set(projectAtom, { ...project, suggestedKeeperCount: count })
			void updateCurrentProject({ suggestedKeeperCount: count })
		}
	},
)

export const manualKeeperIdsAtom = atom<Set<PhotoId>>((get) => {
	const allPhotos = get(allPhotosAtom)
	return new Set(
		allPhotos.filter((photo) => photo.manualCullingTag === "Keeper").map((photo) => photo.id),
	)
})

export const manualNonKeeperIdsAtom = atom<Set<PhotoId>>((get) => {
	const allPhotos = get(allPhotosAtom)
	return new Set<PhotoId>(
		allPhotos
			.filter(
				(photo) => photo.manualCullingTag != null && photo.manualCullingTag !== "Keeper",
			)
			.map((photo) => photo.id),
	)
})

export const minAllowedKeeperCountAtom = atom((get) => get(manualKeeperIdsAtom).size)

export const maxAllowedKeeperCountAtom = atom((get) => {
	const allPhotos = get(allPhotosAtom)
	const manualNonKeeperIds = get(manualNonKeeperIdsAtom)
	return allPhotos.length - manualNonKeeperIds.size
})

export const clampKeeperCount = (count: number, min: number, max: number) =>
	Math.min(max, Math.max(min, count))

export const clampAllowedKeeperCount = (count: number) => {
	const minAllowedKeeperCount = store.get(minAllowedKeeperCountAtom)
	const maxAllowedKeeperCount = store.get(maxAllowedKeeperCountAtom)
	return clampKeeperCount(count, minAllowedKeeperCount, maxAllowedKeeperCount)
}

export const keeperPhotosAtom = atom<PhotoRecord[]>((get) => {
	const allPhotos = get(allPhotosAtom)
	return allPhotos.filter((photo) => getCullingTag(photo) === "Keeper")
})

export const currentClusterIdAtom = atom<PhotoId | undefined>((get) => {
	if (get(isShowingClustersAtom)) {
		const currentPhotoId = get(currentPhotoIdAtom) ?? undefined
		const cullingInfo = currentPhotoId ? get(cullingInfoAtom)[currentPhotoId] : undefined
		const threshold = get(photoClustersThresholdsAtom)
		const clusterFromMap = cullingInfo?.clusterLeaderIdMap?.[threshold]
		const currentClusterId = clusterFromMap ?? currentPhotoId

		return currentClusterId
	} else {
		return undefined
	}
})

export const currentClusterAtom = atom<ClusterInfo | undefined>((get) => {
	const clusterId = get(currentClusterIdAtom)
	if (clusterId) {
		const clusters = get(photoClustersAtom)
		return clusters[clusterId]
	} else {
		return undefined
	}
})

export const currentClusterPhotosAtom = atom<PhotoRecord[]>((get) => {
	const clusterId = get(currentClusterIdAtom)
	const clusters = get(photoClustersAtom)
	const photos = get(photosAtom)

	const currentCluster = clusterId ? clusters[clusterId] : undefined

	if (!currentCluster) {
		return []
	}

	return currentCluster.clusterPhotoIds
		.map((id) => photos[id])
		.filter((photo): photo is PhotoRecord => photo !== undefined)
})

export const clusteredCoverPhotosAtom = atom<PhotoRecord[]>((get) => {
	const clusters = get(photoClustersAtom)
	const photos = get(photosAtom)
	return Object.values(clusters)
		.map((clusterInfo) => {
			return photos[clusterInfo.coverPhotoId]
		})
		.filter((photo): photo is PhotoRecord => photo !== undefined)
})

export const clusteredCoverPhotoIdsAtom = atom<string[]>((get) => {
	return get(clusteredCoverPhotosAtom).map((photo) => photo.id)
})

export const photoClustersAtom = atom<Record<string, ClusterInfo>>((get) => {
	const displayedPhotos = get(displayedPhotosAtom) //already filtered
	const allCullingInfo = get(cullingInfoAtom)

	const clusters: Record<string, ClusterInfo> = {}
	displayedPhotos.map((photo) => {
		// Yuhe: treat a photo as a single cluster when not processed - is it good?
		const clusterLeaderId =
			allCullingInfo[photo.id]?.clusterLeaderIdMap?.[get(photoClustersThresholdsAtom)] ??
			photo.id
		const isPhotoRated =
			(photo.rating && photo.rating > 0) ||
			(photo.colorTag && photo.colorTag !== "No Color") ||
			photo.flag !== "unflagged"

		const isKeeper = getCullingTag(photo) === "Keeper"
		if (clusterLeaderId) {
			const currentCluster = clusters[clusterLeaderId]
			if (currentCluster) {
				clusters[clusterLeaderId] = {
					...currentCluster,
					clusterPhotoIds: [...currentCluster.clusterPhotoIds, photo.id],
					ratedPhotoCount: currentCluster.ratedPhotoCount + (isPhotoRated ? 1 : 0),
					keeperPhotoCount: currentCluster.keeperPhotoCount + (isKeeper ? 1 : 0),
				}
			} else {
				clusters[clusterLeaderId] = {
					clusterLeaderId,
					coverPhotoId: photo.id,
					clusterPhotoIds: [photo.id],
					ratedPhotoCount: isPhotoRated ? 1 : 0,
					keeperPhotoCount: isKeeper ? 1 : 0,
				}
			}
		}
	})

	if (get(sortByScoreInClusterAtom)) {
		const allCullingInfo = get(cullingInfoAtom)

		Object.entries(clusters).map(([id, cluster]) => {
			const sortedPhotoIds = cluster.clusterPhotoIds.sort((a, b) => {
				const aScore = allCullingInfo[a]?.scoreItem?.overallFloat ?? 0
				const bScore = allCullingInfo[b]?.scoreItem?.overallFloat ?? 0
				return bScore - aScore
			})
			clusters[id] = {
				...cluster,
				clusterPhotoIds: sortedPhotoIds,
				coverPhotoId: sortedPhotoIds[0] ?? cluster.coverPhotoId,
			}
		})
	}

	return clusters
})

export const photoClustersListAtom = atom<ClusterInfo[]>((get) => {
	const clusters = get(photoClustersAtom)
	return Object.values(clusters).map((clusterInfo) => {
		return clusterInfo
	})
})

export const clusterPhotoGroupInfoMapAtom = atom<Record<string, ClusterPhotoGroupInfo>>((get) => {
	const clusters = get(photoClustersAtom)
	const result: Record<string, ClusterPhotoGroupInfo> = {}

	for (const cluster of Object.values(clusters)) {
		const total = cluster.clusterPhotoIds.length
		cluster.clusterPhotoIds.forEach((photoId, index) => {
			result[photoId] = {
				clusterLeaderId: cluster.clusterLeaderId,
				index,
				total,
			}
		})
	}

	return result
})

export const selectedGroupedClusterLeaderIdsAtom = atom<Set<PhotoId>>((get) => {
	const selectedPhotoIds = get(selectedPhotosIDsAtom)
	const groupedPhotoInfoMap = get(clusterPhotoGroupInfoMapAtom)
	const selectedGroupedClusterLeaderIds = new Set<PhotoId>()

	selectedPhotoIds.forEach((photoId) => {
		const groupedPhotoInfo = groupedPhotoInfoMap[photoId]
		if (groupedPhotoInfo && groupedPhotoInfo.total > 1) {
			selectedGroupedClusterLeaderIds.add(groupedPhotoInfo.clusterLeaderId)
		}
	})

	return selectedGroupedClusterLeaderIds
})

export const useFacePreviews = (
	project: ProjectItem,
	photo: PhotoRecord | null,
): Preview[] | undefined => {
	const focusedAtom = useMemo(
		() => focusedFacePreviewsAtom(photo?.id ?? "", project.id),
		[photo?.id, project.id],
	)

	const facePreviews = useAtomValue(focusedAtom)

	return facePreviews
}

export interface FacePreview {
	url: string
	storageSize: number
	size: PreviewSize
	digest?: string
}

export interface FacePreviewOptions {
	projectId: string
	photo: PhotoRecord
	data: Blob[]
	size: PreviewSize
	tags: PreviewTag[]
	isForAutoExposure?: boolean
}

export const facePreviewsAtom = atom<Record<UniquePhotoId, Preview[]>>({})
export const focusedFacePreviewsAtom = (photoId: PhotoId, projectId: string) =>
	focusAtom(facePreviewsAtom, (optic) => optic.prop(getUniquePhotoId(photoId, projectId)))

export const storeFacePreviews = (options: FacePreviewOptions) => {
	const { photo, projectId, data, size, tags, isForAutoExposure } = options

	const facePreviews = store.get(facePreviewsAtom)
	const uniquePhotoId = getUniquePhotoId(photo.id, projectId)
	const currentFacePreviews = store.get(focusedFacePreviewsAtom(photo.id, projectId))

	const shouldUpdate =
		!currentFacePreviews ||
		currentFacePreviews.length !== data.length ||
		currentFacePreviews[0]?.digest !== photo.adjustmentsDigest ||
		currentFacePreviews[0]?.size < size ||
		currentFacePreviews[0]?.tags !== tags || //TODO siyao: double check to make sure extracted does not overwrite rendered?
		isForAutoExposure

	if (shouldUpdate) {
		const newFacePreviews: Preview[] = data.map((blob) => ({
			url: URL.createObjectURL(blob),
			digest: photo.adjustmentsDigest ?? undefined,
			size,
			storageSize: blob.size,
			tags,
		}))

		store.set(facePreviewsAtom, {
			...facePreviews,
			[uniquePhotoId]: newFacePreviews,
		})

		if (currentFacePreviews) {
			currentFacePreviews.map(({ url }) => URL.revokeObjectURL(url))
		}
	}
}

// Used to extract previews from an existing thumbnail (fast)
export const extractFacesFromPreview = async (
	projectId: string,
	photo: PhotoRecord,
	bitmap: ImageBitmap,
	faceRects: PaddedFaceRect[],
) => {
	//check if already exists and not do anything
	const currentFacePreviews = store.get(focusedFacePreviewsAtom(photo.id, projectId))
	if (
		currentFacePreviews &&
		currentFacePreviews[0] &&
		currentFacePreviews.length === faceRects.length
	) {
		return
	}

	// cropImage uses a shared OffscreenCanvas on non-Firefox browsers.
	// Running these crops concurrently can race and produce invalid medium previews.
	// Keep this pass sequential so medium previews reliably show while full-size extraction runs.
	const blobs: Blob[] = []
	for (let index = 0; index < faceRects.length; index++) {
		const rect = faceRects[index]
		if (!rect) {
			continue
		}
		const blob = await cropImage(bitmap, {}, rect)
		blobs.push(blob)
	}

	storeFacePreviews({
		projectId: projectId,
		photo,
		data: blobs,
		size: PreviewSize.medium,
		tags: ["original", "extracted"],
	})
}

// Used to extract previews from a loaded half size
export const extractFacesFromLoadedPhotoIfNeeded = async (options: {
	projectId: string
	photo: PhotoRecord
	loadedPhoto: Photo
	isForAutoExposure?: boolean
	signal?: AbortSignal
}) => {
	const { projectId, photo, loadedPhoto, isForAutoExposure, signal } = options

	const cullingInfo = getCullingInfo(photo.id)

	const rects = cullingInfo?.faceInfo ?? []

	//Check if full size face preview already exist
	const currentFacePreviews = store.get(focusedFacePreviewsAtom(photo.id, projectId))
	if (
		currentFacePreviews &&
		currentFacePreviews[0] &&
		currentFacePreviews[0].size === PreviewSize.fullSize &&
		currentFacePreviews[0].digest === photo.adjustmentsDigest &&
		currentFacePreviews.length === rects.length &&
		!isForAutoExposure
	) {
		return
	}

	const editor = getEditor()

	if (rects && rects.length > 0) {
		if (editor.photo.id !== photo.id) {
			return
		}

		const cropper = new CropExtractor(editor.renderer)

		const defaultAdjustments = getDefaultAdjustments()
		const uncroppedPhoto = loadedPhoto.clone({
			adjustments: {
				...loadedPhoto.adjustments,
				crop: defaultAdjustments.crop,
				cropStraighten: defaultAdjustments.cropStraighten,
				perspectiveVertical: defaultAdjustments.perspectiveVertical,
			},
		})

		const faceRects = rects.map((rect) => padFaceRect(rect.faceRect))

		// May turn on async encode to get faster speed but higher memory usage
		const results = await cropper.cropYUV(uncroppedPhoto, faceRects, signal, {
			useAsyncEncode: false,
		})

		if (signal?.aborted) {
			return
		}

		const tags: PreviewTag[] = photo.adjustmentsDigest
			? ["edited", "rendered"]
			: ["rendered", "original"]

		storeFacePreviews({
			projectId,
			photo,
			data: results,
			size: PreviewSize.fullSize,
			tags,
			isForAutoExposure,
		})
	}
}

export const setManualCullingTagForPhotos = async (photoIds: PhotoId[], tag: CullingTag | null) => {
	const photosById = store.get(photosAtom)
	const currentKeeperCountBeforeUpdate = store.get(suggestedKeeperCountAtom)
	let keeperCountDelta = 0
	let updatedPhotoCount = 0

	for (const photoId of photoIds) {
		const photo = photosById[photoId]
		if (!photo) {
			continue
		}
		updatedPhotoCount += 1

		const previousCullingTag = photo.manualCullingTag ?? photo.smartCullingTag
		const nextCullingTag = tag ?? photo.smartCullingTag

		if (previousCullingTag !== "Keeper" && nextCullingTag === "Keeper") {
			keeperCountDelta += 1
		} else if (previousCullingTag === "Keeper" && nextCullingTag !== "Keeper") {
			keeperCountDelta -= 1
		}
	}

	if (updatedPhotoCount === 0) {
		return
	}

	autoAdvanceAfterTaggingIfNeeded(photoIds.length === 1 ? photoIds[0] : undefined)

	await updatePhotos((photos) => {
		for (const photoId of photoIds) {
			photos[photoId] = { manualCullingTag: tag }
		}
	})

	const tagLabel = tag
		? i18n.t(`components:${cullingTagKeys[tag]}`)
		: i18n.t(`components:${CULLING_AUTO_KEY}`)
	showToast(i18n.t("components:photoContextMenu.toastSetCullingTag", { tag: tagLabel }))

	if (keeperCountDelta !== 0) {
		const allPhotos = store.get(allPhotosAtom)
		const minimumRequiredKeeperCount = allPhotos.filter(
			(photo) => photo.manualCullingTag === "Keeper",
		).length
		const nextKeeperCount = Math.min(
			allPhotos.length,
			Math.max(minimumRequiredKeeperCount, currentKeeperCountBeforeUpdate + keeperCountDelta),
		)
		const currentKeeperCount = store.get(suggestedKeeperCountAtom)

		if (nextKeeperCount !== currentKeeperCount) {
			store.set(suggestedKeeperCountAtom, nextKeeperCount)
		}
	}
}

export const updateKeepersAndRejects = async (count: number) => {
	const allPhotos = store.get(allPhotosAtom)
	const suggestKeepers = store.get(isSuggestingKeepersAtom)
	const allCullingInfos = store.get(cullingInfoAtom)
	const cullingInfosWithScores = Object.values(allCullingInfos).filter(
		(val) => val.scoreItem != undefined,
	)
	const rejectIds = getRejectedIds(cullingInfosWithScores)
	const applyRejectsAndCandidates = async () => {
		await updatePhotos((photos) => {
			for (const photoId of rejectIds) {
				photos[photoId] = { smartCullingTag: "Reject" }
			}
			const rejectSet = new Set(rejectIds)
			for (const photo of allPhotos) {
				const cullingInfo = allCullingInfos[photo.id]
				if (!rejectSet.has(photo.id) && cullingInfo?.scoreItem != undefined) {
					photos[photo.id] = { smartCullingTag: "Candidate" }
				}
			}
		})
	}

	if (!suggestKeepers) {
		await applyRejectsAndCandidates()
		return
	}

	const manualKeeperIds = store.get(manualKeeperIdsAtom)
	const manualNonKeeperIds = store.get(manualNonKeeperIdsAtom)
	const clampedCount = clampAllowedKeeperCount(count)
	if (clampedCount !== count) {
		store.set(suggestedKeeperCountAtom, clampedCount)
	}

	const aiKeeperTargetCount = Math.max(0, clampedCount - manualKeeperIds.size)

	if (aiKeeperTargetCount === 0) {
		await applyRejectsAndCandidates()
		return
	}

	const { keeperIds } = selectKeepersOnTheFly(clampedCount, {
		excludedKeeperIds: manualNonKeeperIds,
	})
	const aiKeeperIds = keeperIds
		.filter((photoId) => !manualKeeperIds.has(photoId))
		.slice(0, aiKeeperTargetCount)
	const keeperIdSet = new Set([...manualKeeperIds, ...aiKeeperIds])
	const smartRejectIds = rejectIds.filter((photoId) => !keeperIdSet.has(photoId))

	await updatePhotos((photos) => {
		for (const photoId of aiKeeperIds) {
			photos[photoId] = { smartCullingTag: "Keeper" }
		}

		for (const photoId of smartRejectIds) {
			photos[photoId] = { smartCullingTag: "Reject" }
		}

		const keeperRejectsSet = new Set([...aiKeeperIds, ...smartRejectIds])
		for (const photo of allPhotos) {
			const cullingInfo = allCullingInfos[photo.id]

			if (!keeperRejectsSet.has(photo.id) && cullingInfo?.scoreItem != undefined) {
				photos[photo.id] = { smartCullingTag: "Candidate" }
			}
		}
	})
}

export const generateAutoExposedPreview = (photoId: string) => {
	const editor = getEditor()
	const cullingInfo = store.get(currentCullingInfoAtom)

	let finalAdjustments: Adjustments = editor.adjustments

	finalAdjustments = {
		...finalAdjustments,
		...cullingInfo?.luminanceScoreStats?.autoLightParams,
	}

	void exportImage(editor, {
		...defaultPreviewOptions,
		jpegEncoder: JpegEncoder,
		adjustments: finalAdjustments,
	}).then((data) => {
		if (!data) {
			return
		}

		const url = URL.createObjectURL(data)

		const updatedUrls = {
			[photoId]: url,
		}

		store.set(cullingAutoExposurePreviewUrlsAtom, (prev) => ({
			...prev,
			...updatedUrls,
		}))
	})
}

export const updateKeepersAndRejectsDebounced = debounce(updateKeepersAndRejects, 100)

export const isCullingTagOptionEnabled = (options: {
	tag: CullingTag | null
	isShowingRejectsOrKeepers: boolean
	isShowingKeepers: boolean
	isShowingRejects: boolean
}) => {
	const { tag, isShowingRejectsOrKeepers, isShowingKeepers, isShowingRejects } = options
	if (!isShowingRejectsOrKeepers) {
		return false
	}
	if (tag === "Keeper") {
		return isShowingKeepers
	}
	if (tag === "Reject") {
		return isShowingRejects
	}
	return true
}

export function getCullingTag(photo: PhotoRecord): CullingTag | null {
	const isShowingKeeper = store.get(isSuggestingKeepersAtom)
	const isShowingRejects = store.get(isShowingRejectsAtom)

	const cullingTag = photo.manualCullingTag ?? photo.smartCullingTag
	if (!cullingTag) {
		return null
	}

	if (!isShowingKeeper && !isShowingRejects) {
		return null
	}
	if (cullingTag === "Keeper" && !isShowingKeeper) {
		return "Candidate"
	}
	if (cullingTag === "Reject" && !isShowingRejects) {
		return "Candidate"
	}
	return cullingTag
}
